import type { MediaFolder } from '@/content'

/**
 * Turns a content value into a description of how to edit it.
 *
 * ## Why infer rather than declare
 *
 * There are eighteen content sections and they change as the site grows.
 * Hand-writing a form schema for each would mean every new field needs
 * editing in two places, and the admin panel silently falls behind the site
 * the first time someone forgets. Inference reads the shape from the data
 * that is actually there, so a field added to `src/data` is editable the
 * moment it ships — which is the same property that made a raw JSON editor
 * tempting, without making the owner of the site write JSON.
 *
 * ## Where inference is not enough
 *
 * A string is a string: nothing in the value says whether it is a headline,
 * a paragraph, a URL or an image. Those come from the key name, in the
 * tables below. Getting one wrong costs a slightly awkward input, never
 * data loss — the value is still a string either way.
 */

export type FieldKind =
  | 'text'
  | 'choice'
  | 'longText'
  | 'number'
  | 'boolean'
  | 'url'
  | 'image'
  | 'file'
  | 'tags'
  | 'list'
  | 'group'

export interface Field {
  key: string
  label: string
  kind: FieldKind
  /** Cloudinary subfolder this field's uploads belong in. */
  folder?: MediaFolder
  /** For `group` and `list`: the shape of the object being edited. */
  fields?: Field[]
  /** For `list`: a blank item, so "Add" produces something editable. */
  template?: unknown
}

/** Keys whose string value is a picture, and should get an uploader. */
const IMAGE_KEYS = new Set([
  'cover',
  'portrait',
  'heroimage',
  'mark',
  'avatar',
  'image',
  'logo',
  'photo',
  'thumbnail',
  // A project's wide banner, and the `src` of each picture in its gallery.
  'reference',
  'src',
])

/** Keys whose string value is a downloadable document rather than a picture. */
const FILE_KEYS = new Set(['resume', 'cv', 'document', 'attachment', 'pdf'])

/**
 * Keys whose value is chosen from a set rather than typed.
 *
 * What the set *is* lives in `vocabulary.ts` and is resolved per render,
 * because one of these depends on a sibling field: the categories on offer
 * are the categories of the domain chosen above them.
 */
const CHOICE_KEYS = new Set([
  'icon',
  'domain',
  'category',
  'projectslug',
  // A nav path with no route behind it is a link to a 404.
  'path',
  // Not references, but values that recur: offering what is already in use
  // is how "ESIGN/UIECC" stays one client rather than becoming three.
  'client',
  'organisation',
  'institution',
  'issuer',
  'level',
  'service',
  'year',
])

/** Keys whose string value is a link. */
const URL_KEYS = new Set([
  'url',
  'href',
  'link',
  'bookingurl',
  'website',
  'repo',
])

/** Keys that hold prose and want room to breathe. */
const LONG_KEYS = new Set([
  'bio',
  'summary',
  'description',
  'overview',
  'conclusion',
  'quote',
  'tagline',
  'detail',
  'body',
  'abstract',
  'note',
  'statement',
])

/** The field a list item is named by, in order of preference. */
const TITLE_KEYS = [
  'title',
  'name',
  // A gallery picture has no title; its caption is what it is called.
  'caption',
  'label',
  'heading',
  'role',
  'term',
  'organisation',
  // A classification is nothing but a domain and a category, so without
  // these every row in the list collapses to "Item 1" and the list tells you
  // nothing about what is in it.
  'domain',
  'category',
  'id',
  'slug',
]

/**
 * Where each field's uploads are filed in Cloudinary.
 *
 * Derived from the key rather than chosen in the UI: the owner should not
 * have to think about folder structure to replace a photograph, and a
 * structure nobody has to maintain is the only kind that stays tidy. Keys
 * not listed land in `misc`.
 */
const FOLDER_BY_KEY: Record<string, MediaFolder> = {
  reference: 'projects',
  portrait: 'portraits',
  heroimage: 'portraits',
  photo: 'portraits',
  mark: 'brand',
  logo: 'brand',
  cover: 'projects',
  avatar: 'people',
  resume: 'documents',
  cv: 'documents',
  document: 'documents',
  attachment: 'documents',
  pdf: 'documents',
}

/**
 * Sections with a folder of their own win over the key table.
 *
 * A project's logo belongs in `projects/` beside the rest of that project's
 * pictures, not in `brand/` with the site's own mark — `logo` alone cannot
 * tell you which, but the section being edited can. Documents are the
 * exception and always file as documents, wherever they were uploaded from.
 */
const SECTION_FOLDER: Record<string, MediaFolder> = {
  projects: 'projects',
  research: 'research',
}

const folderFor = (key: string, sectionKey?: string): MediaFolder => {
  const lower = key.toLowerCase()
  if (FILE_KEYS.has(lower)) return FOLDER_BY_KEY[lower] ?? 'documents'

  const bySection = sectionKey ? SECTION_FOLDER[sectionKey] : undefined
  if (bySection) return bySection

  return FOLDER_BY_KEY[lower] ?? 'misc'
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** `heroImage` -> `Hero image`, `skillGroups` -> `Skill groups`. */
export const humanise = (key: string): string => {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

/** A readable name for one item in a list. */
export const titleOf = (item: unknown, index: number): string => {
  if (isPlainObject(item)) {
    for (const key of TITLE_KEYS) {
      const value = item[key]
      if (typeof value === 'string' && value.trim()) return value
    }
  }
  if (typeof item === 'string' && item.trim()) return item
  return `Item ${index + 1}`
}

/**
 * Merges the keys of every item in a list.
 *
 * Reading only the first item would hide any field the rest have and it
 * lacks — an optional `url` present on two projects out of nine would be
 * uneditable, and invisible, on all of them.
 */
const mergeSample = (items: unknown[]): Record<string, unknown> => {
  const sample: Record<string, unknown> = {}
  for (const item of items) {
    if (!isPlainObject(item)) continue
    for (const [key, value] of Object.entries(item)) {
      const seen = sample[key]
      // Prefer a non-empty example, so the *kind* is inferred from real data
      // rather than from whichever item happened to leave it blank.
      const better =
        seen === undefined ||
        seen === null ||
        seen === '' ||
        (Array.isArray(seen) && seen.length === 0)
      if (better) sample[key] = value
    }
  }
  return sample
}

/**
 * Shapes for lists that are empty in both the value and the default.
 *
 * An empty array looks exactly like an empty list of strings, so it infers
 * as a tag box and the owner gets a text field where a row of pictures
 * belongs. Nothing in the data can settle it — the shape only exists once
 * there is an item, and there can be no item until the form can add one.
 */
const LIST_TEMPLATES: Record<string, unknown> = {
  gallery: { id: '', src: '', caption: '' },
  // A project with no classification yet still needs the two fields that
  // point it at a domain, or it can never be given one.
  classifications: { domain: '', category: '' },
}

const kindOf = (key: string, value: unknown): FieldKind => {
  const lower = key.toLowerCase()

  if (Array.isArray(value)) {
    if (value.length === 0 && LIST_TEMPLATES[lower]) return 'list'
    return value.every((item) => typeof item === 'string') ? 'tags' : 'list'
  }
  if (isPlainObject(value)) return 'group'
  if (typeof value === 'number') return 'number'
  if (typeof value === 'boolean') return 'boolean'

  if (IMAGE_KEYS.has(lower)) return 'image'
  if (FILE_KEYS.has(lower)) return 'file'
  if (CHOICE_KEYS.has(lower)) return 'choice'
  if (URL_KEYS.has(lower)) return 'url'
  if (LONG_KEYS.has(lower)) return 'longText'
  if (typeof value === 'string' && value.length > 90) return 'longText'
  return 'text'
}

/** A blank value of the same shape, for the "Add" button on a list. */
export const blankFrom = (sample: unknown): unknown => {
  if (Array.isArray(sample)) return []
  if (isPlainObject(sample)) {
    const blank: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(sample)) {
      blank[key] = blankFrom(value)
    }
    return blank
  }
  if (typeof sample === 'number') return 0
  if (typeof sample === 'boolean') return false
  return ''
}

/**
 * Shapes for sections that have no example to infer from.
 *
 * Inference reads the shape off the data, which fails in exactly one case:
 * a section that is empty *and* whose compiled-in default is also empty.
 * Testimonials is that case — the invented ones were deleted deliberately —
 * and without a shape "Add" produced a row with no fields in it, which is a
 * dead end rather than an editor.
 *
 * Only add an entry here for a section with no example anywhere. Everything
 * else stays inferred, so a field added to `src/data` is editable the moment
 * it ships rather than when someone remembers to update this.
 */
const FALLBACK_TEMPLATES: Record<string, Record<string, unknown>> = {
  testimonials: { id: '', name: '', role: '', quote: '', avatar: '' },
}

/** Describes the fields of one object. */
export const inferFields = (value: unknown, sectionKey?: string): Field[] => {
  if (!isPlainObject(value)) return []

  return Object.entries(value).map(([key, child]) => {
    const kind = kindOf(key, child)
    const field: Field = { key, label: humanise(key), kind }

    if (kind === 'image' || kind === 'file') {
      field.folder = folderFor(key, sectionKey)
    }

    if (kind === 'group') {
      field.fields = inferFields(child, sectionKey)
    }

    if (kind === 'list' && Array.isArray(child)) {
      // An empty list has no shape of its own; `LIST_TEMPLATES` supplies one
      // so "Add" produces a row with fields rather than an empty box.
      const sample =
        child.length > 0
          ? mergeSample(child)
          : (LIST_TEMPLATES[key.toLowerCase()] as Record<string, unknown>)

      field.fields = inferFields(sample, sectionKey)
      field.template = blankFrom(sample)
    }

    return field
  })
}

/**
 * What shape a whole section has.
 *
 * Four, not two. Most sections are a record or a list of records, but the
 * home page also reads a plain list of words (the marquee) and a single
 * paragraph (the statement beside it). Both were in the content model and
 * neither had an editor, because a section that is not an object had nothing
 * for the field inference to describe — it rendered an empty form.
 */
export type SectionKind = 'record' | 'list' | 'tags' | 'text'

/**
 * Describes a whole section.
 *
 * An empty list is the awkward case: there is nothing to infer a shape from.
 * `fallback` is the value compiled into the site, which always has at least
 * one example — so a section the owner has emptied is still editable, and
 * "Add" still produces the right fields.
 */
export interface SectionShape {
  kind: SectionKind
  fields: Field[]
  template: unknown
}

export const inferSection = (
  value: unknown,
  fallback: unknown,
  /** Used to look up a declared shape when there is nothing to infer from. */
  sectionKey?: string,
): SectionShape => {
  const reference = value !== undefined && value !== null ? value : fallback

  if (Array.isArray(reference)) {
    // A list of plain words is chips, not rows: the marquee and the language
    // list are each one string per entry, and a collapsible card per word
    // would be a filing cabinet for a shopping list.
    if (reference.length > 0 && reference.every((i) => typeof i === 'string')) {
      return { kind: 'tags', fields: [], template: '' }
    }

    const source =
      reference.length > 0 ? reference : Array.isArray(fallback) ? fallback : []
    let sample = mergeSample(source)

    // Nothing in the stored value and nothing in the default: fall back to a
    // declared shape, or "Add" would produce a row with no fields in it.
    if (Object.keys(sample).length === 0 && sectionKey) {
      sample = FALLBACK_TEMPLATES[sectionKey] ?? sample
    }

    return {
      kind: 'list',
      fields: inferFields(sample, sectionKey),
      template: blankFrom(sample),
    }
  }

  // One paragraph of copy, edited as itself rather than as a field of an
  // object it is not part of.
  if (typeof reference === 'string') {
    return { kind: 'text', fields: [], template: '' }
  }

  const source = isPlainObject(value) ? value : fallback
  return { kind: 'record', fields: inferFields(source, sectionKey), template: {} }
}
